import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import InputForm from './InputForm';
import ReportPage from './ReportPage';
import { calculateTransformationValue, getTopInsights } from '../../utils/algorithms';
import { event } from '../../utils/analytics';

function DiagnosticContainer() {
  const [searchParams] = useSearchParams();
  const [validationState, setValidationState] = useState('loading'); // 'loading' | 'valid' | 'invalid' | 'completed'
  const [guidCode, setGuidCode] = useState(null);
  // const [diagnosticType, setDiagnosticType] = useState(null); // Reserved for Phase 3 (multiple diagnostic types)
  const [showReport, setShowReport] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'Senior Leader',
    valueAtStake: '',
    score1: 0, score2: 0, score3: 0, score4: 0, score5: 0, score6: 0, score7: 0,
    score8: 0, score9: 0, score10: 0, score11: 0, score12: 0, score13: 0, score14: 0
  });
  const [calculations, setCalculations] = useState(null);
  const [topInsights, setTopInsights] = useState([]);

  // Validate GUID on component mount
  useEffect(() => {
    const code = searchParams.get('code');

    // If no code provided, allow free access (legacy behavior)
    if (!code) {
      setValidationState('valid');
      return;
    }

    // Validate the GUID code
    const validateCode = async () => {
      try {
        const response = await fetch(`/api/validate-code?code=${code}`);
        const data = await response.json();

        if (data.success && data.valid) {
          setGuidCode(code);
          // setDiagnosticType(data.diagnosticType); // Reserved for Phase 3 (multiple diagnostic types)

          if (data.completed) {
            // Load saved results and show report
            setValidationState('completed');
            // TODO: Populate formData and calculations from data.results
            // For now, just show that it's completed
          } else {
            // Valid and incomplete - pre-fill leader info
            setValidationState('valid');
            setFormData(prev => ({
              ...prev,
              firstName: data.leader.firstName,
              lastName: data.leader.lastName,
              email: data.leader.email,
            }));
          }
        } else {
          setValidationState('invalid');
        }
      } catch (error) {
        console.error('GUID validation error:', error);
        setValidationState('invalid');
      }
    };

    validateCode();
  }, [searchParams]);

  const handleGenerateReport = async () => {
    // Gather all scores
    const scores = {
      score1: formData.score1, score2: formData.score2, score3: formData.score3,
      score4: formData.score4, score5: formData.score5, score6: formData.score6,
      score7: formData.score7, score8: formData.score8, score9: formData.score9,
      score10: formData.score10, score11: formData.score11, score12: formData.score12,
      score13: formData.score13, score14: formData.score14
    };

    // Run calculations
    const calcs = calculateTransformationValue(
      parseFloat(formData.valueAtStake),
      formData.role,
      scores
    );

    const insights = getTopInsights(scores);

    // Save results to database (new API endpoint)
    if (guidCode) {
      try {
        await fetch('/api/save-system-burden-results', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            code: guidCode,
            leadershipRole: formData.role,
            valueAtStake: parseFloat(formData.valueAtStake),
            scores: scores,
            calculations: calcs
          })
        });
      } catch (error) {
        console.error('Failed to save results:', error);
        // Silently continue - don't block user from seeing their report
      }
    } else {
      // Legacy mode: Submit to old Cloudflare Worker (for free access without GUID)
      try {
        await fetch(
          'https://diagnostic-emailer.andrew-kidd.workers.dev/',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              role: formData.role,
              valueAtStake: formData.valueAtStake,
              calculations: calcs,
              scores: scores
            })
          }
        );
      } catch (error) {
        // Silently continue - don't block user from seeing their report
      }
    }

    // Track diagnostic completion
    event('diagnostic_completed', {
      role: formData.role,
      value_at_stake: formData.valueAtStake,
      deficit: calcs.valueDeficit
    });

    setCalculations(calcs);
    setTopInsights(insights);
    setShowReport(true);
    window.scrollTo(0, 0);
  };

  const handleStartOver = () => {
    setShowReport(false);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      role: 'Senior Leader',
      valueAtStake: '',
      score1: 0, score2: 0, score3: 0, score4: 0, score5: 0, score6: 0, score7: 0,
      score8: 0, score9: 0, score10: 0, score11: 0, score12: 0, score13: 0, score14: 0
    });
    setCalculations(null);
    setTopInsights([]);
    window.scrollTo(0, 0);
  };

  // Render loading state
  if (validationState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#062B49] mx-auto mb-4"></div>
          <p className="text-[#697A90]">Validating your diagnostic link...</p>
        </div>
      </div>
    );
  }

  // Render invalid code state
  if (validationState === 'invalid') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h1 className="text-2xl font-poppins font-bold text-[#062B49] mb-4">
            Invalid or Expired Link
          </h1>
          <p className="text-[#697A90] mb-6">
            This diagnostic link is invalid, has expired, or has already been used.
            Please request a new diagnostic link.
          </p>
          <a
            href="/request-diagnostic"
            className="inline-block bg-[#86C2CA] hover:bg-[#3B4F6B] text-white font-poppins font-bold px-6 py-3 rounded transition-all"
          >
            Request New Diagnostic
          </a>
        </div>
      </div>
    );
  }

  // Render completed diagnostic state
  if (validationState === 'completed') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-green-500 text-5xl mb-4">✅</div>
          <h1 className="text-2xl font-poppins font-bold text-[#062B49] mb-4">
            Diagnostic Already Completed
          </h1>
          <p className="text-[#697A90] mb-6">
            You've already completed this diagnostic. Your results have been saved
            and sent to your email.
          </p>
          <p className="text-sm text-[#697A90] mb-6">
            Need to complete another diagnostic?
          </p>
          <a
            href="/request-diagnostic"
            className="inline-block bg-[#86C2CA] hover:bg-[#3B4F6B] text-white font-poppins font-bold px-6 py-3 rounded transition-all"
          >
            Request New Diagnostic
          </a>
        </div>
      </div>
    );
  }

  // Render normal diagnostic flow
  return (
    <div className="min-h-screen bg-gray-50">
      {!showReport ? (
        <InputForm
          formData={formData}
          setFormData={setFormData}
          onGenerateReport={handleGenerateReport}
        />
      ) : (
        <ReportPage
          formData={formData}
          calculations={calculations}
          topInsights={topInsights}
          onStartOver={handleStartOver}
        />
      )}
    </div>
  );
}

export default DiagnosticContainer;